/**
 * Turning an edited snapshot back into a small save request.
 *
 * The settings panels work the way the version editor's do: clone the whole
 * config, change something in it, hand the whole thing back. Posting that
 * verbatim would mean uploading the entire file — several megabytes, nearly all
 * of it base64 assets — on every debounced keystroke.
 *
 * A snapshot's top-level keys split cleanly along the lines edits actually fall
 * on (`components`, `assets`, `generalSettings`, `localization`, …), so a
 * shallow diff over those keys is enough: editing a component sends the
 * components array and leaves the asset payload alone. Comparing an unchanged
 * assets map costs a structural walk over a few long strings, which is cheap
 * next to uploading them.
 */

import _ from "lodash";

/**
 * @returns {{ patches: Array<{path, value}>, removedPaths: string[] }} — empty
 *   arrays when nothing changed, which the caller should treat as "no save".
 */
export function diffSnapshot(previous, next) {
  const patches = [];
  const removedPaths = [];

  const previousKeys = Object.keys(previous || {});
  const nextKeys = Object.keys(next || {});

  for (const key of nextKeys) {
    if (!_.isEqual(_.get(previous, [key]), _.get(next, [key]))) {
      patches.push({ path: key, value: next[key] });
    }
  }

  for (const key of previousKeys) {
    if (!nextKeys.includes(key)) removedPaths.push(key);
  }

  return { patches, removedPaths };
}

/**
 * Merge a new set of patches into the ones still waiting to be sent. A repeated
 * path replaces its earlier entry rather than queueing twice, and moves to the
 * end so the last write wins.
 */
export function mergePatches(pending, incoming) {
  const merged = pending.filter(
    (patch) => !incoming.some((candidate) => candidate.path === patch.path)
  );
  return [...merged, ...incoming];
}
